import React, { ReactNode, useMemo } from 'react';
import ArrayPopup from 'components/ArrayPopup';
import { DataRoomSheet } from 'router/subrouters/DataRoom/models/DataRoom';
import classNames from 'classnames';
import { HEADER_OPTIONS } from './data';
import BulkUploadSelect from 'components/BulkUploadSelect';
import { getArrayOfColleagueEmails } from './utils';
import { checkIsValidEmail, checkIsValidName } from 'utils';

const MAX_LENGTH = 3;

type CellProps = {
  column: string;
  dataRoom: DataRoomSheet;
};

const ColleaguesCell: React.FC<CellProps> = ({ dataRoom }) => {
  const { colleagues } = dataRoom || {};

  const arrayOfColleagueEmails = useMemo(
    () => getArrayOfColleagueEmails(colleagues),
    [colleagues],
  );

  const hasError = useMemo(
    () => !!colleagues && !arrayOfColleagueEmails.length,
    [colleagues, arrayOfColleagueEmails.length],
  );

  if (!arrayOfColleagueEmails.length)
    return (
      <div className={classNames({ 'invalid-cell': hasError })}>
        {colleagues}
      </div>
    );

  return (
    <div className={classNames({ 'invalid-cell': hasError })}>
      <ArrayPopup
        list={arrayOfColleagueEmails}
        maxLength={MAX_LENGTH}
        isOrderedList
        popupText="colleaguesLength"
      />
    </div>
  );
};

const RequiredCell: React.FC<CellProps> = ({ dataRoom, column }) => {
  const el = dataRoom[column as keyof DataRoomSheet];

  return <div className={classNames({ 'invalid-cell': !el })}>{el}</div>;
};

const NameCell: React.FC<CellProps> = ({ dataRoom, column }) => {
  const el = dataRoom[column as keyof DataRoomSheet];

  const hasError = !checkIsValidName(el);

  return <div className={classNames({ 'invalid-cell': hasError })}>{el}</div>;
};

const EmailCell: React.FC<CellProps> = ({ dataRoom, column }) => {
  const el = dataRoom[column as keyof DataRoomSheet];

  const hasError = !checkIsValidEmail(el);

  return <div className={classNames({ 'invalid-cell': hasError })}>{el}</div>;
};

export const KeyCell: React.FC<CellProps> = ({ column, dataRoom }) => {
  const el = dataRoom[column as keyof DataRoomSheet];

  return <div>{el}</div>;
};

export const BODY_CONTENT_GENERATE: Record<
  string,
  (cellProps: CellProps) => ReactNode
> = {
  dataRoomName: (cellProps: CellProps) => <RequiredCell {...cellProps} />,
  ownerFirstName: (cellProps: CellProps) => <NameCell {...cellProps} />,
  ownerLastName: (cellProps: CellProps) => <NameCell {...cellProps} />,
  ownerEmail: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  colleagues: (cellProps: CellProps) => <ColleaguesCell {...cellProps} />,
};

type HeaderProps = {
  index?: number;
  column: string;
  allColumns: string[];
  onUpdateColumn?: (key: string, newValue: string) => void;
};

export const KeyHeader: React.FC<HeaderProps> = (props) => (
  <BulkUploadSelect {...props} options={HEADER_OPTIONS} />
);

export const HEADER_CONTENT_GENERATE: Record<
  string,
  (headerProps: HeaderProps) => ReactNode
> = {
  dataRoomName: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  ownerFirstName: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  ownerLastName: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  ownerEmail: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  colleagues: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
};
